// src/services/reportPatientSearch.js
//
// The weekly report's "find your patient" search. Results show as the
// coordinator types: by name (any order, "Last, First" works, each word
// matching the start of a name part), by date of birth, or by MRN digits.
//
// Works over the site's patients already loaded in the browser
// (loadPatientsForMatching: consents + duplicates + the Sheet on Dubois), so
// nothing extra is sent anywhere.
//
// Pure and Firebase-free so it is unit-tested (reportPatientSearch.test.js).

import { nameTokens, normalizeDob, normalizeMrnDigits } from "./patientDuplicateMatch";

export const MIN_QUERY_LENGTH = 2;

/** Pre-computes what the search compares, once per patient list. */
export const buildSearchIndex = (patients = []) =>
  patients
    .filter((p) => p && (p.name || p.mrn || p.dob))
    .map((p) => ({
      patient: p,
      tokens: nameTokens(p.name),
      dob: normalizeDob(p.dob),
      mrn: normalizeMrnDigits(p.mrn),
    }));

/**
 * Patients matching what was typed, best first, at most `limit`.
 * Fewer than MIN_QUERY_LENGTH characters returns nothing.
 */
export const searchPatients = (index = [], query = "", { limit = 20 } = {}) => {
  const q = String(query || "").trim();
  if (q.length < MIN_QUERY_LENGTH) return [];

  const dob = /\d/.test(q) ? normalizeDob(q) : "";
  if (dob) return index.filter((r) => r.dob === dob).slice(0, limit).map((r) => r.patient);

  if (/^\d[\d\s-]*$/.test(q)) {
    const digits = normalizeMrnDigits(q);
    if (digits.length < 3) return [];
    return index
      .filter((r) => r.mrn && r.mrn.includes(digits))
      .slice(0, limit)
      .map((r) => r.patient);
  }

  const words = nameTokens(q);
  if (words.length === 0) return [];
  const scored = [];
  index.forEach((r) => {
    if (r.tokens.length === 0) return;
    let exact = 0;
    const allMatch = words.every((w) =>
      r.tokens.some((t) => {
        if (t === w) {
          exact += 1;
          return true;
        }
        return t.startsWith(w);
      })
    );
    if (allMatch) scored.push({ r, exact });
  });
  scored.sort(
    (a, b) =>
      b.exact - a.exact ||
      String(a.r.patient.name).localeCompare(String(b.r.patient.name), undefined, { sensitivity: "base" })
  );
  return scored.slice(0, limit).map((s) => s.r.patient);
};

/** "Consent", "Duplicate" or "Sheet": where a result came from. */
export const sourceLabel = (source) =>
  source === "sheet" ? "Sheet" : source === "duplicates" ? "Duplicate" : "Patient list";
